import { FC, useEffect, useMemo } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ChevronRightIcon, ShoppingBagIcon } from '@heroicons/react/outline';
import {
  giftOrderListForStoreDataAtomFamily,
  giftOrderListForStoreStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderListForStoreHook } from '@shared/api/giftOrder/giftOrder.hook';
import {
  GiftOrderForStore,
  GiftOrderItemType,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { formatPrice } from '@shared/utils/formatPrice';
import { Link, useSearchParams } from 'react-router-dom';

import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';

// 결제 상태 한글 변환
const paymentStatusLabel: Record<GiftOrderPaymentStatus, string> = {
  [GiftOrderPaymentStatus.NotPaid]: '미결제',
  [GiftOrderPaymentStatus.Waiting]: '입금대기',
  [GiftOrderPaymentStatus.Paid]: '결제완료',
  [GiftOrderPaymentStatus.Confirmed]: '확정',
  [GiftOrderPaymentStatus.CancelRequested]: '취소요청',
  [GiftOrderPaymentStatus.Canceled]: '취소됨',
};

// 결제 상태 색상
const getStatusColor = (status: GiftOrderPaymentStatus) => {
  switch (status) {
    case GiftOrderPaymentStatus.Paid:
    case GiftOrderPaymentStatus.Confirmed:
      return 'bg-green-100 text-green-800';
    case GiftOrderPaymentStatus.Waiting:
      return 'bg-yellow-100 text-yellow-800';
    case GiftOrderPaymentStatus.Canceled:
    case GiftOrderPaymentStatus.CancelRequested:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// 쿠폰형 상품 포함 여부 확인
const getCouponInfo = (order: GiftOrderForStore) => {
  const couponItems = order.orderDetails.filter(
    (item) => item.type === GiftOrderItemType.Coupon,
  );
  const hasCoupon = couponItems.length > 0;
  const isOnlyCoupon =
    couponItems.length === order.orderDetails.length && order.orderDetails.length > 0;
  const couponCount = couponItems.reduce((sum, item) => sum + item.quantity, 0);

  return { hasCoupon, isOnlyCoupon, couponCount, couponItems };
};

// 주문 카드 컴포넌트
const OrderCard: FC<{ order: GiftOrderForStore; bugoId: string | null }> = ({
  order,
  bugoId,
}) => {
  const firstItem = order.orderDetails[0];
  const totalItems = order.orderDetails.length;
  const totalQuantity = order.orderDetails.reduce((sum, d) => sum + d.quantity, 0);

  // 쿠폰 정보
  const { hasCoupon, isOnlyCoupon, couponCount } = getCouponInfo(order);

  // 상품명 생성
  const productName =
    totalItems > 1
      ? `${firstItem?.giftItemEmbed?.name || '상품'} 외 ${totalItems - 1}건`
      : firstItem?.giftItemEmbed?.name || '상품';

  // 이미지 URL
  const imageUrl =
    typeof firstItem?.giftItem === 'object' &&
    firstItem?.giftItem?.giftItemInfo?.image?.url
      ? firstItem.giftItem.giftItemInfo.image.url
      : typeof firstItem?.giftItem === 'object' && firstItem?.giftItem?.image?.url
      ? firstItem.giftItem.image.url
      : '';

  // 결제 완료 상태인지 확인
  const isPaid =
    order.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
    order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed;

  // 구매내역에서는 항상 주문 상세(쿠폰) 페이지로 이동
  const linkTo = `/gift-store/order/${order._id}/coupon?bugoId=${bugoId}`;

  return (
    <Link
      to={linkTo}
      className="block rounded-lg bg-white p-4 shadow transition hover:shadow-md"
    >
      <div className="flex gap-3">
        {/* 이미지 */}
        <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded bg-gray-100">
          {imageUrl ? (
            <img
              src={fileBucketUrl(imageUrl, 'w200')}
              alt={productName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
              No Image
            </div>
          )}
          {/* 쿠폰 뱃지 */}
          {hasCoupon && (
            <div className="absolute bottom-0 left-0 right-0 bg-purple-600/90 py-0.5 text-center text-[10px] font-bold text-white">
              쿠폰 {couponCount}장
            </div>
          )}
        </div>

        {/* 주문 정보 */}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <p className="break-keep font-medium">{productName}</p>
              <p className="mt-0.5 text-sm text-gray-500">수량: {totalQuantity}개</p>
            </div>
            <span
              className={`break-keep rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(
                order.paymentDetail.status,
              )}`}
            >
              {paymentStatusLabel[order.paymentDetail.status] ||
                order.paymentDetail.status}
            </span>
          </div>

          <div className="mt-2 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              {formatDate(order.createdAt, { dateSeparater: '.' })}
            </p>
            <p className="font-bold text-red-500">
              {formatPrice(order.paymentDetail.paymentByCash)}원
            </p>
          </div>

          {/* 쿠폰 전달 유도 버튼 */}
          {hasCoupon && isPaid && (
            <div className="mt-2 flex items-center justify-end gap-1 text-sm font-medium text-purple-600">
              <span>{isOnlyCoupon ? '답례품 전달하기' : '쿠폰 전달하기'}</span>
              <ChevronRightIcon className="h-4 w-4" />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

const GiftOrderListPage: FC = function GiftOrderListPage() {
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const { storeRole } = useAuth();

  // 주문 목록 조회
  const role = storeRole();
  useGiftOrderListForStoreHook(role);
  const status = useRecoilValue(giftOrderListForStoreStatusAtomFamily(role));
  const orderList = useRecoilValue(giftOrderListForStoreDataAtomFamily(role));

  useEffect(() => {
    setTitle('구매내역');
  }, [setTitle]);

  // 결제 완료된 주문만 필터링
  const filteredOrders = useMemo(() => {
    if (!orderList) return [];
    return orderList.filter(
      (order) =>
        order.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
        order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed ||
        order.paymentDetail.status === GiftOrderPaymentStatus.Waiting,
    );
  }, [orderList]);

  // 로딩 중
  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 주문 목록 */}
      {filteredOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <ShoppingBagIcon className="h-16 w-16 text-gray-300" />
          <p className="mt-4 text-gray-500">구매내역이 없습니다</p>
          <Link
            to={`/gift-store?bugoId=${bugoId}`}
            className="mt-4 rounded-lg bg-red-500 px-6 py-2 text-white"
          >
            쇼핑하러 가기
          </Link>
        </div>
      ) : (
        <div className="space-y-3 p-4">
          {filteredOrders.map((order) => (
            <OrderCard key={order._id} order={order} bugoId={bugoId} />
          ))}
        </div>
      )}
    </div>
  );
};

export default GiftOrderListPage;
